import { onMounted, ref, computed, watch, onBeforeUnmount, onBeforeMount } from 'vue'
import { isEmpty } from 'lodash'
import i18n from 'src/assets/i18n/i18n'
import NoResultFound from 'src/components/general/no-result-found/index.vue'
import LoadingSkeleton from '../../../loader/loadingskeleton'
import CloseIcon from '../../../icons/close/Close.vue'
import { MdTypography } from '@antiqua-dls/vue-components'
import NoResultImage from '../asset/no-result-dropdown.svg' 

const { t } = i18n().global

export default {
    name: 'FouDropdown',
    components: {
        LoadingSkeleton,
        CloseIcon,
        MdTypography,
        NoResultFound
    },
    props: {
        noArrow: {
            type: Boolean,
            default: true
        },
        modelValue: {
            type: [String, Number],
            default: ''
        },
        type: {
            type: String,
            default: 'text'
        },
        options: {
            type: Array,
            required: true
        },
        customClass: {
            type: String,
            default: ''
        },
        customActiveClass: {
            type: String,
            default: ''
        },
        customDropdownClass: {
            type: String,
            default: '',
        },
        defaultValue: {
            type: [String, Object],
            default: ''
        },
        placeholder: {
            type: String,
            default: 'Search'
        },
        smallPlaceHolder: {
            type: String,
            default: 'Search'
        },
        selectAction: {
            type: Function,
            required: true
        },
        name: {
            type: String,
            default: ''
        },
        rules: {
            type: String,
            default: ''
        },
        errorClass: {
            type: [String, Object, Array],
            default: () => []
        },
        searchAction: {
            type: Function,
            default: () => { }
        },
        isOptionLoading: {
            type: Boolean,
            default: false
        },
        inputFocused: {
            type: Function,
            default: () => { }
        },
        errorMessage: {
            type: String,
            default: ''
        },
        isDisabled: {
            type: Boolean,
            default: false
        },
        multipleSelection: {
            type: Boolean,
            default: false
        },
        selectedOptions: {
            type: Array,
            default: () => []
        },
        selectOptionListEmpty: {
            type: Boolean,
            default: false
        },
        genericName: {
            type: String,
            default: ''
        },
        hidePlaceholder: {
            type: Boolean,
            default: false
        },
        showNoResultImage:{
            type: Boolean,
            default: false
        },
        sisDropdown: {
            type: Boolean,
            default: false
        },
        showErrorState: {
            type: Boolean,
            default: true
        },
        showOptionsOnTop: {
            type: Boolean,
            default: false
        },
        allowInput:{
            type: Boolean,
            default: true
        },
        alwaysShowPlaceholder: {
            type: Boolean,
            default: false
        },
        noResultsProps: {
            type: Object,
            default: () => ({})
        }
    },
    emits: ['update:modelValue', 'option:selected', 'option:deselected', 'search:blur', 'check-for-error', 'dropdownToggle', 'selected-options-status', 'dropdownOpened'],
    setup(props, { emit }) {
        const isOpen = ref(false);
        const searchText = ref('');
        const isInputFocused = ref(false);
        const previousValue = ref('');
        const showFirstError = ref(false);
        const dropdown = ref(null);
        const svgArrow = ref(null);
        const svgArrowPath = ref(null);
        const checkedOptions = ref([]);
        const dropDownInput = ref(null);

        const showGenericField = computed(() => {
            if (props.genericName) {
                return props.genericName
            }
            // remove custom code and use generic name prop
            if (props.name === 'headOfficeCountry') {
                return 'country'
            }

            // remove custom code and use generic name prop
            if (props.name === 'headOfficeState') {
                return 'state'
            }

            return props.name
        });

        const dropdownContent = ref(null);

        const settingDropdownContentStyle = () => {
            const rect = dropdown.value.getBoundingClientRect();
            dropdownContent.value.style.top = rect.bottom + window.scrollY + 'px';
            dropdownContent.value.style.left = rect.left + 'px';
            dropdownContent.value.style.width = rect.width + 'px';
        }


        const showDropdown = computed(() => {
            if (dropdownContent.value && props.showOptionsOnTop) {     
                if (isOpen.value) {
                    dropdownContent.value.style.display = 'unset';
                    dropdownContent.value.style.position = 'absolute';
                    dropdownContent.value.style.zIndex = '9999';
                    settingDropdownContentStyle();
                    document.body.appendChild(dropdownContent.value);
                    emit('dropdownToggle', true);
                }
                else {
                    dropdownContent.value.style.display = 'none';
                    emit('dropdownToggle', false);
                }
            }

            if (props.sisDropdown && !searchText.value.length) {
                return isOpen.value
            }
            if (props.multipleSelection && searchText.value.length <= 2) {
                return;
            }
            if (props.type === 'text') {
                return isOpen.value && (filteredOptions.value.length > 0 || props.isOptionLoading || searchText.value.length > 2)
            }
            return isOpen.value
        });

        const showEmptyError = computed(() =>
            filteredOptions.value.length === 0 &&
            !props.isOptionLoading &&
            searchText.value.length > 2 &&
            !props.sisDropdown
        );

        const showEmptyErrorMessage = computed(() => {
            if (showEmptyError.value && props.name !== 'family designers') {
                return t('pages.home.components.fouDropdown.no_values')
            }

            return t('pages.home.components.fouDropdown.designers_not_found')
        })

        watch(() => searchText.value, (newValue) => {
            emit('update:modelValue', newValue)
        });

        watch(() => props.defaultValue, (newValue) => {
            if (props.type === 'text') {
                searchText.value = props.options.find((x) => x.value == newValue)?.label || '';
            }
            if (isEmpty(newValue)) {
                showFirstError.value = true
                emit('check-for-error', showFirstError.value)
            } else {
                showFirstError.value = false
                emit('check-for-error', showFirstError.value)
            }
        }, { immediate: true, deep: true });

        watch(() => props.selectedOptions, (newValue) => {
            triggered(newValue);
        }, { deep: true });

        const filteredOptions = computed(() => {
            const searchTerm = typeof searchText.value == 'object' ? searchText.value?.label?.toLowerCase() : searchText.value?.toLowerCase();
            let isMatching = false
            props.options?.forEach(option => {
                if (option.label.toLowerCase() == (searchTerm)) {
                    isMatching = false
                }
            })
            if (searchTerm?.length > 0 && !isMatching) {
                return props.options?.filter(option =>
                    option.label.toLowerCase().includes(searchTerm)
                )
            }
            return props.options
        });

        const selectedOption = computed(() => {
            return props.options && (
                props.options.find(option =>
                    typeof props.defaultValue === 'object' && props.defaultValue !== null
                        ? option.value === props.defaultValue.value
                        : option.value === props.defaultValue
                ) || {}
            );
        });
        const isValid = computed(() => {
            return props.options.find((option) => option?.label.toLowerCase().includes(searchText.value.toLowerCase()))
        });

        const toggleDropdown = () => {
            if (props.isDisabled) return
            if (props.type === 'text') {
                isOpen.value = !isOpen.value;
                // Only emit when opening the dropdown, not when closing
                if (isOpen.value) {
                    emit('dropdownOpened');
                }
            }
            const root = document.getElementById('app');
            if (isOpen.value) {
                clearInput()
                if (window.innerWidth < 720 && root) {
                    root.style.overflow = 'hidden';
                }
            } else {
                isInputFocused.value = false
                root.style.overflow = 'unset';
            }
        }

        const filterOptions = () => {
            isOpen.value = true;
            props.searchAction(searchText.value);
        }

        const normalizeLabel = (label) => label.toLowerCase().replace(/\s+/g, ' ').trim();

        const selectOption = (option) => {
            if (props.multipleSelection) {
                if (props.selectedOptions.some((x) => x.id === option.designerId)) return
            }
            const root = document.getElementById('app');
            previousValue.value = searchText.value
            if (root) {
                root.style.overflow = 'unset';
            }
            if (props.type === 'text') {
                if (!props.alwaysShowPlaceholder) {
                    searchText.value = option.label
                }
                props.selectAction(option);
                isOpen.value = false
                return;
            }

            if (checkedOptions.value.length === 0) {
                checkedOptions.value = props.defaultValue
            }
            const optionIndex = checkedOptions.value.findIndex(opt => normalizeLabel(opt.label) === normalizeLabel(option.label));
            if (optionIndex !== -1) {
                checkedOptions.value.splice(optionIndex, 1);
                emit('option:deselected', checkedOptions.value);
            } else {
                const checkedValues = Array.isArray(checkedOptions?.value) ? checkedOptions.value : [];
                const defaultValues = Array.isArray(props.defaultValue) ? props.defaultValue : [];

                const combinedOptions = new Set([
                    ...checkedValues,
                    ...defaultValues,
                    option
                ]);

                checkedOptions.value = Array.from(combinedOptions);
                emit('option:selected', checkedOptions.value);
            }
        }

        const closeDropdownOnClickOutside = (e) => {
            const isClickOutsideArrow = props.noArrow ?
                (svgArrow.value && !svgArrow.value.contains(e.target) && svgArrowPath.value && !svgArrowPath.value.contains(e.target)) :
                true;
            if (!dropdown.value.contains(e.target) && isClickOutsideArrow && isOpen.value) {
                isOpen.value = false
                isInputFocused.value = false
                if (props.defaultValue != null) {
                    searchText.value = props.options.find((x) => x.value == props.defaultValue)?.label || ''
                } else {
                    searchText.value = ''
                }
                const root = document.getElementById('app');
                if (window.innerWidth < 720 && root) {
                    root.style.overflow = 'unset';
                }
            }
        }

        const selected = (option) => {
            if (props.multipleSelection) {
                return props.selectedOptions.some((x) => x.id === option.designerId)
            }
            if (props.type === 'text') {
                return typeof props.defaultValue === 'object' && props.defaultValue !== null ? option.value === props.defaultValue.value : option.value === props.defaultValue
            }
            if (checkedOptions.value.length === 0) {
                return props.defaultValue.some((x) => normalizeLabel(x.label) === normalizeLabel(option.label))
            }
            return checkedOptions.value.some((x) => x.value === option.value)
        }

        const clearInput = () => {
            if (props.type === 'text') {
                searchText.value = ''
            }
            isInputFocused.value = true
            props.inputFocused();
        }

        const triggered = (selectedOption) => {
            if (selectedOption.length || !showFirstError.value) {
                emit('selected-options-status', true)
            } else {
                emit('selected-options-status', false)
            }
        }

        const onBlur = () => {
            if (isValid.value && props.type === 'text') {
                let currentVal = typeof props.defaultValue === 'object' && props.defaultValue !== null ? props.defaultValue.value : props.defaultValue
                if (currentVal != null) {
                    searchText.value = props.options.find((x) => x.value.toLowerCase() == currentVal.toLowerCase())?.label || ''
                } else {
                    searchText.value = ''
                }
                isOpen.value = false
            }
            isInputFocused.value = false

            if (props.multipleSelection) {
                showFirstError.value = isEmpty(props.selectedOptions);
            } else {
                showFirstError.value = isEmpty(props.defaultValue);
            }
            
            emit('check-for-error', showFirstError.value)
            triggered(props.selectedOptions)
            emit('search:blur', searchText.value)
        }

        const addBlurriness = () => {
            if (props.isDisabled) return;

            if (dropDownInput.value) {
                isInputFocused.value = true;
                dropDownInput.value.focus();
            }
        }

        const enterPressed = () => {
            let enteredOptionArr = props.options.filter(option => option.label.toLowerCase() == searchText.value.toLowerCase())
            if (!enteredOptionArr.length) return
            let enteredOptionObj = {
                value: enteredOptionArr[0].value,
                label: enteredOptionArr[0].label
            }
            selectOption(enteredOptionObj);
            isInputFocused.value = false
        }

        onBeforeMount(() => {
            searchText.value = selectedOption.value?.label || ''
            showFirstError.value = false
            emit('check-for-error', showFirstError.value)
        });

        onMounted(() => {
            document.addEventListener('mousedown', closeDropdownOnClickOutside);
            if (props.showOptionsOnTop) {
                document.addEventListener('scroll', settingDropdownContentStyle);
                document.addEventListener('resize', settingDropdownContentStyle);
            }
        });

        onBeforeUnmount(() => {
            document.removeEventListener('mousedown', closeDropdownOnClickOutside);
            if (props.showOptionsOnTop) {
                document.removeEventListener('scroll', settingDropdownContentStyle);
                document.removeEventListener('resize', settingDropdownContentStyle);
            }
        });


        return {
            showEmptyError,
            showEmptyErrorMessage,
            checkedOptions,
            showDropdown,
            isOpen,
            searchText,
            isInputFocused,
            previousValue,
            showFirstError,
            showGenericField,
            filteredOptions,
            selectedOption,
            isValid,
            dropdown,
            dropDownInput,
            svgArrow,
            svgArrowPath,
            addBlurriness,
            selected,
            toggleDropdown,
            filterOptions,
            selectOption,
            triggered,
            closeDropdownOnClickOutside,
            clearInput,
            onBlur,
            enterPressed,
            dropdownContent,
            NoResultImage
        }
    }
}